// ─── Native social sign-in (Apple + Google) ──────────────────────────────────
//
// Wraps @capgo/capacitor-social-login and hands the returned identity token to
// Supabase's signInWithIdToken. This is the NATIVE flow (real iOS device): the
// OS presents the Apple/Google sheet, we get an ID token back, and Supabase
// verifies it server-side. No browser redirect — which is why this sidesteps the
// capacitor:// redirect problems that pushed email verification to code-only.
//
// After a successful sign-in Supabase sets a session, AuthContext's
// onAuthStateChange picks it up, and Login.js's routing effect sends the user to
// their home (returning user) or to /role-select → profile creation (brand-new
// user, whose profile row doesn't exist yet — fetchProfile returns null). So no
// extra routing is needed here.
//
// WEB: the native plugin's web fallback needs extra config (an Apple Services ID
// + redirect for Apple; a Google web flow). We currently gate the buttons to
// native platforms only (see socialAuthAvailable) and can enable web later.
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { supabase } from './supabase';
import { GOOGLE_WEB_CLIENT_ID, GOOGLE_IOS_CLIENT_ID, WEB_SOCIAL_ENABLED } from './config';

// Only offer the buttons where the native sheets exist. On plain web this is
// false, so Login.js hides them until web social is configured.
export const socialAuthAvailable = () => Capacitor.isNativePlatform() || WEB_SOCIAL_ENABLED;

// ─── Name pre-fill (App Store Guideline 4 / Sign in with Apple) ──────────────
// Apple hands back the user's name ONLY on the first authorization, in the
// plugin's `result.profile` — never again, and never in the ID-token claims that
// Supabase stores. So we capture it at sign-in and stash it for the onboarding
// (CreateProfile) screen to pre-fill, instead of forcing the user to re-type a
// name Authentication Services already provided. Google's name is stashed too for
// symmetry (Supabase also exposes it via user_metadata, used as a fallback).
const PREFILL_KEY = 'nomad.social.namePrefill';
function stashSocialName(givenName, familyName) {
  const full = [givenName, familyName].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  try {
    if (full) localStorage.setItem(PREFILL_KEY, full);
    else localStorage.removeItem(PREFILL_KEY); // returning user w/o name → drop any stale value
  } catch { /* localStorage unavailable — pre-fill is best-effort */ }
}
export function readSocialNamePrefill() {
  try { return localStorage.getItem(PREFILL_KEY) || ''; } catch { return ''; }
}
export function clearSocialNamePrefill() {
  try { localStorage.removeItem(PREFILL_KEY); } catch { /* no-op */ }
}

let initPromise = null;
function ensureInit() {
  // initialize() is idempotent-ish but should run once; cache the promise so
  // concurrent taps don't double-initialize.
  if (!initPromise) {
    initPromise = SocialLogin.initialize({
      // iOS native Apple sign-in needs no client config.
      apple: {},
      google: {
        // Google's WEB client ID is the audience Supabase validates against, so
        // it's required on every platform. The iOS client ID is what the native
        // Google sheet uses on-device.
        webClientId: GOOGLE_WEB_CLIENT_ID,
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
      },
    }).catch((e) => { initPromise = null; throw e; });
  }
  return initPromise;
}

// Normalize the plugin's cancellation into a sentinel the UI can swallow quietly.
function isCancellation(err) {
  const m = (err && (err.message || String(err))) || '';
  return /cancel|canceled|cancelled|1001|dismiss/i.test(m);
}
export const SOCIAL_CANCELLED = 'SOCIAL_CANCELLED';

// Nonce helpers for native Google sign-in. Google puts the SHA-256 DIGEST of the
// nonce into the ID token; Supabase re-hashes the RAW nonce we hand it and
// compares. So: digest -> Google's login call, raw -> Supabase's signInWithIdToken.
// Without this pairing Supabase rejects with "Passed nonce and nonce in id_token
// should either both exist or not."
function randomNonce(bytes = 32) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}
async function sha256Hex(input) {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function signInWithApple() {
  if (!Capacitor.isNativePlatform()) {
    // Web: Supabase hosted OAuth redirect. The browser navigates to Apple and
    // back to redirectTo; supabase-js (detectSessionInUrl) resolves the session
    // on return and AuthContext picks it up. Requires the Apple *web* config
    // (Services ID + key) in Supabase — see the runbook's web section.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return null; // page redirects away; nothing more to do here
  }
  await ensureInit();
  let res;
  try {
    res = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
  } catch (err) {
    if (isCancellation(err)) throw new Error(SOCIAL_CANCELLED);
    throw err;
  }
  // Capture the name Apple provided (first sign-in only) so onboarding can
  // pre-fill it — see the note by PREFILL_KEY.
  stashSocialName(res?.result?.profile?.givenName, res?.result?.profile?.familyName);
  const identityToken = res?.result?.identityToken || res?.result?.idToken;
  if (!identityToken) throw new Error('Apple sign-in did not return an identity token.');
  const { data, error } = await supabase.auth.signInWithIdToken({ provider: 'apple', token: identityToken });
  if (error) throw error;
  return data;
}

export async function signInWithGoogle() {
  if (!Capacitor.isNativePlatform()) {
    // Web: Supabase hosted OAuth redirect (same pattern as Apple above).
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return null;
  }
  await ensureInit();
  try {
    return await googleNativeAttempt();
  } catch (err) {
    if (err?.message === SOCIAL_CANCELLED) throw err;
    // iOS caches the Google credential, so a repeat sign-in can hand back a token
    // minted with the PREVIOUS nonce -> Supabase throws "Nonces mismatch". Clear
    // the cached credential and retry once with a fresh nonce.
    if (/nonce/i.test(err?.message || '')) {
      try { await SocialLogin.logout({ provider: 'google' }); } catch (_) { /* not signed in */ }
      return await googleNativeAttempt();
    }
    throw err;
  }
}

// One native Google attempt: fresh nonce -> plugin login -> Supabase exchange.
// (Declared after signInWithGoogle; function declarations hoist, so it's callable.)
async function googleNativeAttempt() {
  const rawNonce = randomNonce();
  const nonceDigest = await sha256Hex(rawNonce);
  let res;
  try {
    res = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'], nonce: nonceDigest } });
  } catch (err) {
    if (isCancellation(err)) throw new Error(SOCIAL_CANCELLED);
    throw err;
  }
  // Symmetry with Apple: stash the Google-provided name for onboarding pre-fill.
  stashSocialName(res?.result?.profile?.givenName, res?.result?.profile?.familyName);
  const idToken = res?.result?.idToken;
  if (!idToken) throw new Error('Google sign-in did not return an ID token.');
  const { data, error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken, nonce: rawNonce });
  if (error) throw error;
  return data;
}
